// Chat pane for the Brand Graph Builder surface.
//
// Simpler than IntakePane: the graph_builder agent emits a small SSE
// event set (text / tool_use / artifact / done / error). No clarifier
// flow, no orchestrator transitions, no todo lists — the diff card IS
// the primary surface for proposals, the chat is just the rationale.
//
// Layout: stacked turns above, composer pinned bottom. Each assistant
// turn renders as: optional tool-use pills + text segments + proposal
// cards interleaved in arrival order.
//
// Attachments: paperclip → uploads via /chat/v2/refs/{sessionId} (PDFs
// go through the same `ref_*` id space now). Sent refs travel as
// composer attachments on the next turn.

import {
  ArrowUp,
  FileText,
  Image as ImageIcon,
  Layers,
  Loader2,
  Paperclip,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useStore } from "@/lib/store";
import type {
  GraphNode,
  GraphProposal,
  GraphSnapshot,
  GraphStreamEvent,
  GraphVerificationArtifact,
} from "@/lib/types";
import { cn } from "@/lib/utils";

import { AskUserQuestionCard } from "./AskUserQuestionCard";
import { ContradictionCard } from "./ContradictionCard";
import { GraphProposalCard } from "./GraphProposalCard";
import { GraphTypeProposalCard } from "./GraphTypeProposalCard";
import { InlineNodeChip, parseTextWithChips } from "./InlineNodeChip";
import { VerificationImageCard } from "./VerificationImageCard";

interface Props {
  sessionId: string;
  tenantId: string;
  /** User-pinned mode override. When undefined the server infers from
   * node count (<10 → onboarding, else living). */
  sessionMode?: "onboarding" | "living";
  /** Latest snapshot from the parent — used by VerificationImageCard
   * to resolve contributing_node_ids → display labels. */
  snapshot?: GraphSnapshot | null;
  /** Called after every apply / reject so the parent can re-fetch the
   * snapshot + history and update the right pane. */
  onProposalResolved: () => void;
  /** Called when a proposal is staged so the parent can also re-fetch
   * pending list if desired. Optional. */
  onProposalStaged?: (proposal: GraphProposal) => void;
  /** Called on every agent tool_use event to push spotlight focus into
   * the parent — the BrandAtlas dims everything except the nodes /
   * categories the agent is currently touching. */
  onFocusChange?: (focus: {
    nodeIds: string[];
    serverCategories: string[];
  }) => void;
}

type SegmentKind =
  | "text"
  | "tool_use"
  | "proposal"
  | "ask_user_question"
  | "verification";

interface Segment {
  kind: SegmentKind;
  /** For "text" segments. */
  text?: string;
  /** For "tool_use" segments. */
  tool?: string;
  toolInput?: Record<string, unknown>;
  /** When >1, this tool_use segment is a GROUPED summary of N
   * consecutive same-tool calls within a turn. Renders as
   * "Inspecting N nodes" pill instead of one per call. */
  count?: number;
  /** For "proposal" segments. */
  proposalId?: string;
  /** For "ask_user_question" segments — full payload so AskUserQuestionCard
   * can render + POST back to /chat/v2/answer. */
  askData?: {
    qid: string;
    thread_id: string;
    questions: Array<{
      id?: string;
      question: string;
      options?: Array<string | { label: string; description?: string }>;
      multi?: boolean;
    }>;
  };
  /** For "verification" segments — the agent's brand-grounded test image. */
  verification?: GraphVerificationArtifact;
}

interface Turn {
  id: string;
  role: "user" | "assistant";
  /** Plain user text (assistant text lives inside segments). */
  text?: string;
  /** Composer attachments shown above the user bubble. */
  attachments?: Array<{ kind: "image" | "document"; name: string; url?: string }>;
  /** Interleaved segments for assistant turns. */
  segments?: Segment[];
  /** Set to true while the SDK is producing this turn. */
  streaming?: boolean;
  /** Error text if the stream failed mid-turn. */
  error?: string;
}

interface PendingAttachment {
  localId: string;
  file: File;
  kind: "image" | "document";
  previewUrl?: string;
  uploadState: "uploading" | "ready" | "error";
  serverId?: string;
  errorMessage?: string;
}

interface ResolvedState {
  applied: Record<
    string,
    {
      affected: number;
      withInstances?: boolean;
      /** flag_contradiction proposals: which resolution the user picked. */
      resolution?: "keep_both" | "delete_a" | "delete_b";
    }
  >;
  rejected: Record<string, string>;
}

const uid = () => Math.random().toString(36).slice(2, 10);
const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const MAX_PDF_BYTES = 30 * 1024 * 1024; // matches server PDF_INLINE_MAX_BYTES

/** Delay before clearing the spotlight after a turn ends. The focus
 *  is union'd across the whole turn (so the agent's working area
 *  stays lit while it's actually working), and only relaxes back to
 *  neutral after this delay post-turn-end. 8s feels right for a long
 *  read-then-explain pattern; tune if it feels too sticky. */
const FOCUS_CLEAR_MS = 8000;

/** Inspect a tool_use event and return the spotlight-worthy targets.
 *  Only graph_builder tools contribute; everything else returns nulls.
 *  Returns the combined set so a single tool call can light up multiple
 *  things (e.g. flag_contradiction lights both node ids). */
function extractFocus(
  tool: string,
  input: Record<string, unknown> | undefined
): { nodeIds: string[]; serverCategories: string[] } {
  const empty = { nodeIds: [], serverCategories: [] };
  if (!input) return empty;
  // Strip the MCP prefix for legibility
  const t = tool.replace(/^mcp__inku_graph__/, "");

  const nodeIds: string[] = [];
  const serverCategories: string[] = [];

  const addNode = (v: unknown) => {
    if (typeof v === "string" && v.startsWith("node_")) nodeIds.push(v);
  };
  const addCat = (v: unknown) => {
    if (typeof v === "string" && v.length > 0) serverCategories.push(v);
  };

  switch (t) {
    case "get_node":
      addNode(input.node_id);
      break;
    case "list_nodes_in_category":
      addCat(input.category);
      break;
    case "propose_node_edit": {
      const kind = String(input.kind || "");
      if (kind === "create") {
        addCat(input.category);
      } else if (kind === "create_batch") {
        // Items array carries the per-item categories
        const items = Array.isArray(input.items) ? input.items : [];
        for (const item of items) {
          if (item && typeof item === "object") {
            addCat((item as Record<string, unknown>).category);
          }
        }
      } else if (kind === "update" || kind === "delete") {
        addNode(input.target_node_id);
      }
      break;
    }
    case "propose_link_edit":
      addNode(input.from_id);
      addNode(input.to_id);
      break;
    case "propose_new_node_type":
      // We don't yet have a node id for the new type. The atlas can't
      // pre-highlight a category that doesn't exist on the graph, so
      // skip focusing here — the new sector will appear after apply.
      break;
    case "flag_contradiction":
      addNode(input.node_a_id);
      addNode(input.node_b_id);
      break;
    case "generate_test_image":
      addNode(input.photography_style_node_id);
      break;
    case "analyze_image_for_style":
      // The category field on this tool is one of the VLM-friendly
      // strings; map those to server-side category names so the atlas
      // highlights the right sector.
      switch (String(input.category || "")) {
        case "photography_style":
        case "color_grading":
          // Color grading anchors land under PhotographyStyle on the
          // graph (they're shoot/post-processing characteristics, not
          // brand palette swatches).
          addCat("PhotographyStyle");
          break;
        case "color_palette":
          addCat("ColorPalette");
          break;
        // mood / composition don't map cleanly to a sector — skip.
      }
      break;
    case "parse_pdf":
    case "fetch_url":
    case "list_brand_graph":
    default:
      // No targeted entity — the agent is exploring broadly.
      break;
  }

  if (nodeIds.length === 0 && serverCategories.length === 0) return empty;
  return { nodeIds, serverCategories };
}

// localStorage keys, scoped per session_id, so multiple browser tabs
// (or future multi-session work) don't trample each other.
const turnsKey = (sessionId: string) => `inku.graph.turns.${sessionId}`;
const proposalsKey = (sessionId: string) => `inku.graph.proposals.${sessionId}`;
const resolvedKey = (sessionId: string) => `inku.graph.resolved.${sessionId}`;

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
function saveJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota / private mode — silently drop. Chat continues working
    // for the session even if persistence fails.
  }
}

function classify(file: File): "image" | "document" | null {
  if (file.type.startsWith("image/")) return "image";
  if (
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf")
  ) {
    return "document";
  }
  return null;
}

export function GraphChatPane({
  sessionId,
  tenantId,
  sessionMode,
  snapshot,
  onProposalResolved,
  onProposalStaged,
  onFocusChange,
}: Props) {
  const { setToast } = useStore();

  // Hydrate turns + proposals + resolved-state from localStorage on
  // mount. Persistence makes the page resilient to refresh — the SDK
  // session is already resumed by id, and now the UI history matches.
  // We also drop any half-streamed assistant turn (`streaming: true`)
  // on load: it's stale and Claude will reply fresh on the next user
  // turn.
  const [turns, setTurns] = useState<Turn[]>(() => {
    const stored = loadJson<Turn[]>(turnsKey(sessionId), []);
    return stored.map((t) =>
      t.streaming ? { ...t, streaming: false } : t
    );
  });
  // Map proposalId → full proposal for diff-card rendering.
  const [proposals, setProposals] = useState<Record<string, GraphProposal>>(
    () => loadJson<Record<string, GraphProposal>>(proposalsKey(sessionId), {})
  );
  const [resolved, setResolved] = useState<ResolvedState>(() =>
    loadJson<ResolvedState>(resolvedKey(sessionId), {
      applied: {},
      rejected: {},
    })
  );

  useEffect(() => {
    // Strip blob previews + the File object from attachments before
    // persisting — Files can't be serialized, and previewUrl is a
    // session-scoped blob URL that would 404 after a refresh anyway.
    const sanitized = turns.map((t) => ({
      ...t,
      attachments: t.attachments?.map((a) => ({
        kind: a.kind,
        name: a.name,
        // intentionally drop the url; it would dangle after refresh
      })),
    }));
    saveJson(turnsKey(sessionId), sanitized);
  }, [turns, sessionId]);

  useEffect(() => {
    saveJson(proposalsKey(sessionId), proposals);
  }, [proposals, sessionId]);

  useEffect(() => {
    saveJson(resolvedKey(sessionId), resolved);
  }, [resolved, sessionId]);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [composerText, setComposerText] = useState("");
  const [busy, setBusy] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  // Holds the focus-clear timer so we can cancel it when a new tool_use
  // arrives mid-turn (otherwise focus would flicker off between tool
  // calls). Cleared on done/error.
  const focusClearTimerRef = useRef<number | null>(null);
  // Accumulated focus across the current turn. We UNION every touched
  // node id and category so the atlas keeps showing what we worked on
  // for the duration of the turn — not just the single most-recent
  // tool call. Cleared on done/error.
  const currentFocusRef = useRef<{
    nodeIds: Set<string>;
    serverCategories: Set<string>;
  }>({ nodeIds: new Set(), serverCategories: new Set() });

  // Auto-scroll to bottom on new turn or new segment. Cheap: just scroll
  // the container on every turns change.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns]);

  // Auto-resize the textarea up to the max-h-40 cap (160px), then let
  // it scroll internally. Standard "shrink to fit" pattern: reset to
  // auto first so the height can decrease on backspace too.
  const COMPOSER_MAX_PX = 160;
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, COMPOSER_MAX_PX) + "px";
  }, [composerText]);

  // Build a label-to-node resolver for InlineNodeChip. Maps
  // case-insensitive display_label → node so the agent's `[honest]`
  // syntax in chat text resolves to the right node. Computed once per
  // snapshot change.
  const labelResolver = useMemo<(label: string) => GraphNode | null>(() => {
    if (!snapshot) return () => null;
    const all = Object.values(snapshot.nodes_by_category ?? {}).flat() as GraphNode[];
    const byLabel = new Map<string, GraphNode>();
    for (const n of all) {
      const key = (n.display_label || n.content || "").trim().toLowerCase();
      if (key && !byLabel.has(key)) byLabel.set(key, n);
    }
    return (label: string) => {
      const key = label.trim().toLowerCase();
      return byLabel.get(key) ?? null;
    };
  }, [snapshot]);

  // ─── Attachment upload ───────────────────────────────────────────────

  const onPickFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files);
      const fresh: PendingAttachment[] = [];
      for (const file of arr) {
        const kind = classify(file);
        if (!kind) {
          setToast({
            kind: "error",
            text: `${file.name}: only images and PDFs are supported here.`,
          });
          continue;
        }
        const cap = kind === "image" ? MAX_IMAGE_BYTES : MAX_PDF_BYTES;
        if (file.size > cap) {
          setToast({
            kind: "error",
            text: `${file.name}: exceeds ${Math.round(cap / 1024 / 1024)}MB cap.`,
          });
          continue;
        }
        fresh.push({
          localId: uid(),
          file,
          kind,
          previewUrl: kind === "image" ? URL.createObjectURL(file) : undefined,
          uploadState: "uploading",
        });
      }
      if (fresh.length === 0) return;
      setPending((prev) => [...prev, ...fresh]);

      // Fire uploads in parallel.
      for (const p of fresh) {
        try {
          const result = await api.uploadRefs(sessionId, [p.file]);
          const item = result[0];
          if (!item || !item.id) {
            throw new Error("upload returned no id");
          }
          setPending((prev) =>
            prev.map((x) =>
              x.localId === p.localId
                ? { ...x, uploadState: "ready", serverId: item.id }
                : x
            )
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setPending((prev) =>
            prev.map((x) =>
              x.localId === p.localId
                ? { ...x, uploadState: "error", errorMessage: msg }
                : x
            )
          );
        }
      }
    },
    [sessionId, setToast]
  );

  const removePending = (localId: string) => {
    setPending((prev) => {
      const target = prev.find((p) => p.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  };

  // ─── Send ────────────────────────────────────────────────────────────

  const send = useCallback(async () => {
    if (busy) return;
    const text = composerText.trim();
    const readyRefs = pending
      .filter((p) => p.uploadState === "ready" && p.serverId)
      .map((p) => p.serverId!);
    if (!text && readyRefs.length === 0) return;

    const stillUploading = pending.some(
      (p) => p.uploadState === "uploading"
    );
    if (stillUploading) {
      setToast({
        kind: "error",
        text: "Wait for uploads to finish before sending.",
      });
      return;
    }

    const userTurn: Turn = {
      id: uid(),
      role: "user",
      text,
      attachments: pending
        .filter((p) => p.uploadState === "ready")
        .map((p) => ({
          kind: p.kind,
          name: p.file.name,
          url: p.previewUrl,
        })),
    };
    const assistantTurn: Turn = {
      id: uid(),
      role: "assistant",
      segments: [],
      streaming: true,
    };
    setTurns((prev) => [...prev, userTurn, assistantTurn]);
    setComposerText("");
    setPending([]);
    setBusy(true);
    // Reset accumulated spotlight focus at the start of every new turn
    // so we don't carry over stale highlights from the previous turn.
    if (focusClearTimerRef.current !== null) {
      window.clearTimeout(focusClearTimerRef.current);
      focusClearTimerRef.current = null;
    }
    currentFocusRef.current = { nodeIds: new Set(), serverCategories: new Set() };
    if (onFocusChange) {
      onFocusChange({ nodeIds: [], serverCategories: [] });
    }

    try {
      await api.graph.chat({
        sessionId,
        tenantId,
        sessionMode,
        message: text || "(uploaded files, no message)",
        refIds: readyRefs,
        onEvent: (ev) => handleStreamEvent(ev, assistantTurn.id),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantTurn.id
            ? { ...t, error: msg, streaming: false }
            : t
        )
      );
    } finally {
      setBusy(false);
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantTurn.id ? { ...t, streaming: false } : t
        )
      );
    }
  }, [busy, composerText, pending, sessionId, tenantId, sessionMode, setToast]);

  // Helper used by stream-event handler: clear the spotlight after a
  // delay so the user can see the last-touched element. Cancelled on
  // any new tool_use. Also wipes the accumulator so the NEXT turn
  // starts with a fresh focus set.
  const scheduleFocusClear = () => {
    if (!onFocusChange) return;
    if (focusClearTimerRef.current !== null) {
      window.clearTimeout(focusClearTimerRef.current);
    }
    focusClearTimerRef.current = window.setTimeout(() => {
      currentFocusRef.current = { nodeIds: new Set(), serverCategories: new Set() };
      onFocusChange({ nodeIds: [], serverCategories: [] });
      focusClearTimerRef.current = null;
    }, FOCUS_CLEAR_MS);
  };

  // Clean up any pending focus-clear when the component unmounts.
  useEffect(() => {
    return () => {
      if (focusClearTimerRef.current !== null) {
        window.clearTimeout(focusClearTimerRef.current);
      }
    };
  }, []);

  const handleStreamEvent = (ev: GraphStreamEvent, assistantTurnId: string) => {
    setTurns((prev) =>
      prev.map((turn) => {
        if (turn.id !== assistantTurnId) return turn;
        const segments = [...(turn.segments ?? [])];

        switch (ev.type) {
          case "text": {
            const last = segments[segments.length - 1];
            if (last && last.kind === "text") {
              segments[segments.length - 1] = {
                ...last,
                text: (last.text ?? "") + ev.text,
              };
            } else {
              segments.push({ kind: "text", text: ev.text });
            }
            break;
          }
          case "tool_use": {
            segments.push({
              kind: "tool_use",
              tool: ev.tool,
              toolInput: ev.input,
            });
            // Push spotlight focus to the atlas. We UNION every touched
            // entity within a turn — as long as the agent is working
            // on the same area, that area stays lit. Cleared only on
            // done/error + a short delay.
            if (onFocusChange) {
              const f = extractFocus(ev.tool, ev.input);
              if (f.nodeIds.length > 0 || f.serverCategories.length > 0) {
                // Cancel any pending focus-clear; we're still active.
                if (focusClearTimerRef.current !== null) {
                  window.clearTimeout(focusClearTimerRef.current);
                  focusClearTimerRef.current = null;
                }
                for (const id of f.nodeIds) currentFocusRef.current.nodeIds.add(id);
                for (const c of f.serverCategories) currentFocusRef.current.serverCategories.add(c);
                onFocusChange({
                  nodeIds: Array.from(currentFocusRef.current.nodeIds),
                  serverCategories: Array.from(
                    currentFocusRef.current.serverCategories
                  ),
                });
              }
            }
            break;
          }
          case "artifact": {
            // Store the proposal and reference it via a segment so order
            // is preserved relative to text + tool_use.
            setProposals((p) => ({ ...p, [ev.proposal.id]: ev.proposal }));
            segments.push({ kind: "proposal", proposalId: ev.proposal.id });
            onProposalStaged?.(ev.proposal);
            break;
          }
          case "verification": {
            segments.push({
              kind: "verification",
              verification: ev.verification,
            });
            break;
          }
          case "verification_progress": {
            // Surface as a tool-use-ish pill so users see progress
            // through the long Luma poll. We reuse the tool_use
            // segment shape with a synthetic tool name.
            if (ev.phase === "submitting" || ev.phase === "submitted") {
              segments.push({
                kind: "tool_use",
                tool: `mcp__inku_graph__generate_test_image:${ev.phase}`,
              });
            }
            break;
          }
          case "ask_user_question": {
            // The SDK is now paused inside _make_can_use_tool's awaited
            // future. The AskUserQuestionCard POSTs the user's answers
            // to /chat/v2/answer (shared route), which resolves the
            // future and the SDK loop resumes — text deltas will flow
            // again on the same stream.
            segments.push({
              kind: "ask_user_question",
              askData: {
                qid: ev.qid,
                thread_id: ev.thread_id,
                questions: ev.questions,
              },
            });
            break;
          }
          case "error": {
            // Clear spotlight after a short delay so the user sees the
            // last-touched element before the dimming relaxes.
            scheduleFocusClear();
            return {
              ...turn,
              segments,
              error: ev.detail || ev.message,
              streaming: false,
            };
          }
          case "done": {
            scheduleFocusClear();
            return { ...turn, segments, streaming: false };
          }
          case "start":
            // No-op for now — the start event carries model + mode info
            // we could surface in the page header if useful.
            break;
        }
        return { ...turn, segments };
      })
    );
  };

  // ─── Proposal resolution ─────────────────────────────────────────────

  const handleApply = async (
    proposalId: string,
    selectedIndices?: number[]
  ) => {
    try {
      const result = await api.graph.applyProposal(proposalId, {
        tenantId,
        selectedIndices,
      });
      setResolved((prev) => ({
        ...prev,
        applied: {
          ...prev.applied,
          [proposalId]: { affected: result.affected_node_ids.length },
        },
      }));
      setToast({
        kind: "ok",
        text: `Applied · ${result.affected_node_ids.length} node${result.affected_node_ids.length === 1 ? "" : "s"} updated`,
      });
      onProposalResolved();
    } catch (err) {
      setToast({
        kind: "error",
        text: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleApplyType = async (
    proposalId: string,
    withInstances: boolean
  ) => {
    try {
      const result = await api.graph.applyProposal(proposalId, {
        tenantId,
        applyInstances: withInstances,
      });
      setResolved((prev) => ({
        ...prev,
        applied: {
          ...prev.applied,
          [proposalId]: {
            affected: result.affected_node_ids.length,
            withInstances,
          },
        },
      }));
      setToast({
        kind: "ok",
        text: withInstances
          ? `Type registered + ${result.affected_node_ids.length - 1} instance${result.affected_node_ids.length - 1 === 1 ? "" : "s"}.`
          : "Type registered.",
      });
      onProposalResolved();
    } catch (err) {
      setToast({
        kind: "error",
        text: err instanceof Error ? err.message : String(err),
      });
    }
  };

  // ─── Verification reactions ──────────────────────────────────────────
  // "Looks right" / "Doesn't match" don't hit a dedicated endpoint —
  // they pre-fill the composer with a natural-language message and the
  // user can edit/send. Agent reasons over it on the next turn. This
  // is intentional: the verification flow stays conversational, not
  // tagged-mutation.

  const handleVerificationLooksRight = (v: GraphVerificationArtifact) => {
    const label = v.node_display_label || v.node_content || v.node_id;
    setComposerText(
      `The verification image for ${JSON.stringify(label)} looks right — the anchor captures the brand's feel. Keep the node as is.`
    );
    composerRef.current?.focus();
  };

  const handleVerificationRevise = (v: GraphVerificationArtifact) => {
    const label = v.node_display_label || v.node_content || v.node_id;
    setComposerText(
      `The verification image for ${JSON.stringify(label)} doesn't match. The brand feels [describe how it should differ]. Please revise the anchor.`
    );
    composerRef.current?.focus();
  };

  /** Push contributing nodes into the spotlight focus on hover so the
   *  atlas lights them up. Cancels any pending auto-clear so the
   *  highlight stays visible as long as the user is hovering. Empty
   *  array = stop overriding (atlas relaxes back to whatever focus
   *  the streaming tool_use accumulator currently holds). */
  const handleVerificationHover = (nodeIds: string[]) => {
    if (!onFocusChange) return;
    if (nodeIds.length === 0) {
      // Restore the accumulator state (or empty if turn ended)
      onFocusChange({
        nodeIds: Array.from(currentFocusRef.current.nodeIds),
        serverCategories: Array.from(currentFocusRef.current.serverCategories),
      });
      return;
    }
    // Cancel any pending clear so the highlight doesn't blink off
    if (focusClearTimerRef.current !== null) {
      window.clearTimeout(focusClearTimerRef.current);
      focusClearTimerRef.current = null;
    }
    onFocusChange({ nodeIds, serverCategories: [] });
  };

  const handleApplyContradiction = async (
    proposalId: string,
    resolution: "keep_both" | "delete_a" | "delete_b"
  ) => {
    try {
      const result = await api.graph.applyProposal(proposalId, {
        tenantId,
        resolution,
      });
      setResolved((prev) => ({
        ...prev,
        applied: {
          ...prev.applied,
          [proposalId]: {
            affected: result.affected_node_ids.length,
            resolution,
          },
        },
      }));
      setToast({
        kind: "ok",
        text: `Resolution applied: ${resolution.replace("_", " ")}`,
      });
      onProposalResolved();
    } catch (err) {
      setToast({
        kind: "error",
        text: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleReject = async (proposalId: string, userReason?: string) => {
    try {
      await api.graph.rejectProposal(proposalId, {
        tenantId,
        userReason,
      });
      setResolved((prev) => ({
        ...prev,
        rejected: { ...prev.rejected, [proposalId]: userReason ?? "" },
      }));
      setToast({
        kind: "ok",
        text: "Rejected.",
      });
      onProposalResolved();
    } catch (err) {
      setToast({
        kind: "error",
        text: err instanceof Error ? err.message : String(err),
      });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Turns */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-5">
          {turns.length === 0 && <EmptyChatHint />}
          {turns.map((turn) =>
            turn.role === "user" ? (
              <UserBubble key={turn.id} turn={turn} />
            ) : (
              <AssistantBubble
                key={turn.id}
                turn={turn}
                proposals={proposals}
                resolved={resolved}
                snapshot={snapshot ?? null}
                labelResolver={labelResolver}
                onApply={handleApply}
                onApplyType={handleApplyType}
                onApplyContradiction={handleApplyContradiction}
                onVerificationLooksRight={handleVerificationLooksRight}
                onVerificationRevise={handleVerificationRevise}
                onVerificationHover={handleVerificationHover}
                onReject={handleReject}
              />
            )
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-border bg-card px-4 py-3">
        <div className="max-w-2xl mx-auto">
          {pending.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {pending.map((p) => (
                <PendingChip
                  key={p.localId}
                  pending={p}
                  onRemove={() => removePending(p.localId)}
                />
              ))}
            </div>
          )}
          <div
            className={cn(
              "flex items-end gap-2 rounded-2xl border border-border bg-background p-2",
              "focus-within:border-slate-400 transition-colors"
            )}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files.length > 0) {
                onPickFiles(e.dataTransfer.files);
              }
            }}
          >
            <label
              className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground cursor-pointer shrink-0"
              title="Attach PDF or image"
            >
              <Paperclip className="h-4 w-4" />
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    onPickFiles(e.target.files);
                    e.target.value = "";
                  }
                }}
              />
            </label>
            <textarea
              ref={composerRef}
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !busy) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Describe your brand, paste a URL, or drop a PDF…"
              className={cn(
                "flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground/70 leading-relaxed",
                "overflow-y-auto py-1.5"
              )}
              disabled={busy}
              style={{ minHeight: "28px", maxHeight: "160px" }}
            />
            <Button
              size="sm"
              onClick={send}
              disabled={
                busy ||
                (!composerText.trim() && pending.every((p) => p.uploadState !== "ready"))
              }
              className="h-8 w-8 rounded-lg bg-slate-900 hover:bg-slate-800 p-0 shrink-0"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────

function EmptyChatHint() {
  return (
    <div className="text-center px-6 py-20 max-w-md mx-auto">
      <div className="text-xl font-medium text-foreground mb-2">
        Build the brand graph by chatting.
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed">
        Tell the agent about your voice, values, audience. Drop in a
        guidelines PDF or a reference image. Each proposal lands as a
        diff card you can apply or reject — nothing commits until you
        confirm.
      </div>
    </div>
  );
}

function UserBubble({ turn }: { turn: Turn }) {
  return (
    <div className="flex flex-col items-end gap-1.5">
      {turn.attachments && turn.attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 max-w-[80%]">
          {turn.attachments.map((att, i) =>
            att.kind === "image" && att.url ? (
              <img
                key={i}
                src={att.url}
                alt={att.name}
                className="h-16 w-16 rounded-md object-cover border border-border"
              />
            ) : (
              <div
                key={i}
                className="px-2.5 py-1.5 rounded-md bg-muted text-xs text-muted-foreground flex items-center gap-1.5 border border-border"
              >
                <FileText className="h-3 w-3" />
                {att.name}
              </div>
            )
          )}
        </div>
      )}
      {turn.text && (
        <div className="max-w-[80%] rounded-2xl bg-slate-900 text-white px-4 py-2.5 text-sm whitespace-pre-wrap">
          {turn.text}
        </div>
      )}
    </div>
  );
}

function AssistantBubble({
  turn,
  proposals,
  resolved,
  snapshot,
  labelResolver,
  onApply,
  onApplyType,
  onApplyContradiction,
  onVerificationLooksRight,
  onVerificationRevise,
  onVerificationHover,
  onReject,
}: {
  turn: Turn;
  proposals: Record<string, GraphProposal>;
  resolved: ResolvedState;
  snapshot: GraphSnapshot | null;
  labelResolver: (label: string) => GraphNode | null;
  onApply: (proposalId: string, selectedIndices?: number[]) => Promise<void> | void;
  onApplyType: (proposalId: string, withInstances: boolean) => Promise<void> | void;
  onApplyContradiction: (
    proposalId: string,
    resolution: "keep_both" | "delete_a" | "delete_b"
  ) => Promise<void> | void;
  onVerificationLooksRight: (v: GraphVerificationArtifact) => void;
  onVerificationRevise: (v: GraphVerificationArtifact) => void;
  onVerificationHover: (nodeIds: string[]) => void;
  onReject: (proposalId: string, userReason?: string) => Promise<void> | void;
}) {
  const rawSegments = turn.segments ?? [];
  // Group consecutive same-name tool_use segments into a single
  // "Inspecting N nodes" pill. The atlas spotlight already shows the
  // detail; one pill in chat is enough.
  const segments = groupConsecutiveToolUses(rawSegments);
  return (
    <div className="space-y-2">
      {segments.map((seg, i) => {
        if (seg.kind === "text") {
          return (
            <CollapsibleText
              key={i}
              text={seg.text ?? ""}
              resolveLabel={labelResolver}
              onHoverNode={onVerificationHover}
            />
          );
        }
        if (seg.kind === "tool_use") {
          // A grouped tool_use (multiple consecutive same-tool calls)
          // carries a count; render as a compact "Inspecting N nodes"
          // pill instead of one pill per call.
          if (seg.count && seg.count > 1) {
            return (
              <ToolUsePill
                key={i}
                tool={seg.tool ?? ""}
                toolInput={seg.toolInput}
                count={seg.count}
              />
            );
          }
          return (
            <ToolUsePill
              key={i}
              tool={seg.tool ?? ""}
              toolInput={seg.toolInput}
            />
          );
        }
        if (seg.kind === "ask_user_question" && seg.askData) {
          return <AskUserQuestionCard key={i} data={seg.askData} />;
        }
        if (seg.kind === "verification" && seg.verification) {
          return (
            <VerificationImageCard
              key={i}
              verification={seg.verification}
              snapshot={snapshot}
              onLooksRight={onVerificationLooksRight}
              onRevise={onVerificationRevise}
              onHoverBreakdown={onVerificationHover}
            />
          );
        }
        if (seg.kind === "proposal" && seg.proposalId) {
          const proposal = proposals[seg.proposalId];
          if (!proposal) return null;
          const isApplied = resolved.applied[seg.proposalId];
          const isRejected = resolved.rejected[seg.proposalId];
          const resolvedState = isApplied
            ? proposal.kind === "new_node_type"
              ? ({
                  kind: "applied",
                  affected: isApplied.affected,
                  with_instances: isApplied.withInstances ?? true,
                } as const)
              : ({ kind: "applied", affected: isApplied.affected } as const)
            : isRejected !== undefined
              ? ({ kind: "rejected", reason: isRejected } as const)
              : undefined;

          if (proposal.kind === "flag_contradiction") {
            return (
              <ContradictionCard
                key={i}
                proposal={proposal}
                onApply={(resolution) =>
                  onApplyContradiction(seg.proposalId!, resolution)
                }
                onReject={(reason) => onReject(seg.proposalId!, reason)}
                resolved={
                  isApplied
                    ? {
                        kind: "applied",
                        resolution:
                          (isApplied.resolution as
                            | "keep_both"
                            | "delete_a"
                            | "delete_b"
                            | undefined) ?? "keep_both",
                        affected: isApplied.affected,
                      }
                    : isRejected !== undefined
                      ? { kind: "rejected", reason: isRejected }
                      : undefined
                }
              />
            );
          }
          if (proposal.kind === "new_node_type") {
            return (
              <GraphTypeProposalCard
                key={i}
                proposal={proposal}
                onApply={(withInstances) =>
                  onApplyType(seg.proposalId!, withInstances)
                }
                onReject={(reason) => onReject(seg.proposalId!, reason)}
                resolved={
                  resolvedState?.kind === "applied"
                    ? {
                        kind: "applied",
                        affected: resolvedState.affected,
                        with_instances:
                          "with_instances" in resolvedState
                            ? resolvedState.with_instances ?? true
                            : true,
                      }
                    : resolvedState?.kind === "rejected"
                      ? { kind: "rejected", reason: resolvedState.reason }
                      : undefined
                }
              />
            );
          }
          return (
            <GraphProposalCard
              key={i}
              proposal={proposal}
              onApply={(selected) => onApply(seg.proposalId!, selected)}
              onReject={(reason) => onReject(seg.proposalId!, reason)}
              resolved={
                resolvedState?.kind === "applied"
                  ? { kind: "applied", affected: resolvedState.affected }
                  : resolvedState?.kind === "rejected"
                    ? { kind: "rejected", reason: resolvedState.reason }
                    : undefined
              }
            />
          );
        }
        return null;
      })}
      {turn.streaming && segments.length === 0 && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Thinking…
        </div>
      )}
      {turn.error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {turn.error}
        </div>
      )}
    </div>
  );
}

function ToolUsePill({
  tool,
  toolInput,
  count,
}: {
  tool: string;
  toolInput?: Record<string, unknown>;
  count?: number;
}) {
  // Skill calls get a distinct treatment: indigo accent + sparkle icon
  // + the actual skill name pulled from the tool input. Makes it obvious
  // to the user which playbook the agent is consulting.
  if (tool === "Skill") {
    const skillName =
      typeof toolInput?.skill === "string"
        ? toolInput.skill
        : typeof toolInput?.name === "string"
          ? toolInput.name
          : null;
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px]"
        style={{
          background: "oklch(0.96 0.04 280)",
          border: "1px solid oklch(0.62 0.14 280 / 0.3)",
          color: "oklch(0.35 0.14 280)",
        }}
      >
        <Sparkles className="h-3 w-3" />
        <span>
          Using skill
          {skillName && (
            <>
              {": "}
              <span className="font-semibold">{skillName}</span>
            </>
          )}
        </span>
        {count && count > 1 && (
          <span className="text-[10px] tabular-nums opacity-80 ml-0.5">
            ×{count}
          </span>
        )}
      </div>
    );
  }
  const display = toolDisplayName(tool);
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/60 border border-border text-[11px] text-muted-foreground">
      <Layers className="h-3 w-3" />
      <span>{display}</span>
      {count && count > 1 && (
        <span className="text-[10px] tabular-nums opacity-80 ml-0.5">
          ×{count}
        </span>
      )}
    </div>
  );
}

/** Fold consecutive tool_use segments that share the same tool name
 *  into a single grouped segment. Lets us render "Inspecting N nodes"
 *  instead of N identical pills. Non-tool-use segments are passed
 *  through unchanged. */
function groupConsecutiveToolUses(segs: Segment[]): Segment[] {
  const out: Segment[] = [];
  for (const s of segs) {
    if (s.kind !== "tool_use") {
      out.push(s);
      continue;
    }
    const prev = out[out.length - 1];
    // For Skill calls, key the grouping on the skill *name* too — two
    // different skills back-to-back should render as two pills, not one
    // merged "×2" pill that hides which playbooks were used.
    const skillKey = (seg: Segment): string => {
      if (seg.tool !== "Skill") return seg.tool ?? "";
      const inp = seg.toolInput;
      const name =
        (inp && typeof inp.skill === "string" && inp.skill) ||
        (inp && typeof inp.name === "string" && inp.name) ||
        "";
      return `Skill:${name}`;
    };
    if (
      prev &&
      prev.kind === "tool_use" &&
      skillKey(prev) === skillKey(s) &&
      // Don't collapse verification-progress synthetic events (they
      // carry phase info in the tool name); each one is informative.
      !(s.tool ?? "").startsWith("mcp__inku_graph__generate_test_image:")
    ) {
      prev.count = (prev.count ?? 1) + 1;
    } else {
      out.push({ ...s, count: 1 });
    }
  }
  return out;
}

/** Render assistant text with `[label]` inline chips. Long paragraphs
 *  (>300 chars) collapse to first ~2 lines with a "show more" toggle.
 *  Empty / short text passes through plain. */
function CollapsibleText({
  text,
  resolveLabel,
  onHoverNode,
}: {
  text: string;
  resolveLabel: (label: string) => GraphNode | null;
  onHoverNode?: (nodeIds: string[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 320;
  const visible = expanded || !isLong ? text : text.slice(0, 240).trimEnd() + "…";
  const tokens = parseTextWithChips(visible, resolveLabel);

  return (
    <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
      {tokens.map((t, i) => {
        if (t.kind === "text") return <span key={i}>{t.text}</span>;
        if (t.kind === "bold") {
          return (
            <strong key={i} className="font-semibold text-foreground">
              {t.text}
            </strong>
          );
        }
        return (
          <InlineNodeChip
            key={i}
            label={t.label}
            node={t.node}
            onHover={onHoverNode}
          />
        );
      })}
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="ml-1 text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          {expanded ? "show less" : "show more"}
        </button>
      )}
    </div>
  );
}

function toolDisplayName(tool: string): string {
  // Strip the MCP prefix for legibility.
  const stripped = tool.replace(/^mcp__inku_graph__/, "");
  // Verification progress pills travel as a synthetic tool name like
  // "generate_test_image:submitted".
  if (stripped.startsWith("generate_test_image:")) {
    const phase = stripped.split(":")[1];
    if (phase === "submitting") return "Generating verification image…";
    if (phase === "submitted") return "Luma is rendering (30-60s)";
    return `Verification image · ${phase}`;
  }
  switch (stripped) {
    case "parse_pdf":
      return "Reading PDF";
    case "analyze_image_for_style":
      return "Analyzing image";
    case "fetch_url":
      return "Fetching URL";
    case "list_brand_graph":
      return "Reading graph";
    case "list_nodes_in_category":
      return "Reading category";
    case "get_node":
      return "Reading node";
    case "propose_node_edit":
      return "Drafting proposal";
    case "propose_link_edit":
      return "Drafting link";
    case "propose_new_node_type":
      return "Drafting new type";
    case "flag_contradiction":
      return "Flagging contradiction";
    case "generate_test_image":
      return "Generating verification image";
    case "TodoWrite":
      return "Updating plan";
    default:
      return stripped || tool;
  }
}

function PendingChip({
  pending,
  onRemove,
}: {
  pending: PendingAttachment;
  onRemove: () => void;
}) {
  return (
    <div
      className={cn(
        "relative group rounded-md border border-border bg-card flex items-center gap-2 px-2 py-1.5 max-w-[200px]",
        pending.uploadState === "error" && "border-rose-300 bg-rose-50"
      )}
    >
      {pending.kind === "image" && pending.previewUrl ? (
        <img
          src={pending.previewUrl}
          alt={pending.file.name}
          className="h-8 w-8 rounded object-cover shrink-0"
        />
      ) : (
        <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0">
          {pending.kind === "image" ? (
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          ) : (
            <FileText className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs truncate font-medium text-foreground">
          {pending.file.name}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {pending.uploadState === "uploading" && (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
              Uploading…
            </span>
          )}
          {pending.uploadState === "ready" && "Ready"}
          {pending.uploadState === "error" && (
            <span className="text-rose-600">{pending.errorMessage}</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="h-5 w-5 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground shrink-0"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
